import React, { useEffect, useState } from "react";
import { Plus, Trash, PencilSimple, FloppyDisk, X, CircleNotch, FilePdf, DeviceMobile, CaretDown, CaretRight, Fire } from "@phosphor-icons/react";
import axios from "../../utils/axiosInstance";

const API = `${window.location.origin}/api`;

const DEVICE_TYPES = [
  { value: "telefon", label: "Telefon" },
  { value: "laptop", label: "Laptop" },
  { value: "komputer", label: "Komputer" },
  { value: "zegarek", label: "Zegarek" },
  { value: "słuchawki", label: "Słuchawki" },
  { value: "inne", label: "Inne" },
];

const EMPTY_FORM = {
  typ: "telefon",
  model: "",
  numer_seryjny: "",
  data_zakupu: "",
  employee_id: "",
  uwagi: "",
};

export default function UrzadzeniaTab() {
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [expandedProtokoly, setExpandedProtokoly] = useState({});
  const [przekazItem, setPrzekazItem] = useState(null);
  const [przekazForm, setPrzekazForm] = useState({ date: "", place: "", stan_techniczny: "Dobry", uwagi: "" });
  const [przekazSaving, setPrzekazSaving] = useState(false);
  const [zniszczenieItem, setZniszczenieItem] = useState(null);
  const [zniszczenieForm, setZniszczenieForm] = useState({ date: "", place: "", powod: "", sposob: "", uwagi: "" });
  const [zniszcSaving, setZniszcSaving] = useState(false);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [urz, emps] = await Promise.all([
        axios.get(`${API}/urzadzenia`),
        axios.get(`${API}/employees`),
      ]);
      setItems(urz.data);
      setEmployees(emps.data);
    } catch (err) {
      console.error(err);
      alert("Błąd podczas ładowania danych");
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      typ: item.typ || "telefon",
      model: item.model || "",
      numer_seryjny: item.numer_seryjny || "",
      data_zakupu: item.data_zakupu || "",
      employee_id: item.employee_id || "",
      uwagi: item.uwagi || "",
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.typ) return alert("Wybierz typ urządzenia");
    setSaving(true);
    try {
      const payload = { ...form, employee_id: form.employee_id || null };
      if (editing) {
        await axios.put(`${API}/urzadzenia/${editing.id}`, payload);
      } else {
        await axios.post(`${API}/urzadzenia`, payload);
      }
      setShowModal(false);
      setEditing(null);
      setForm(EMPTY_FORM);
      fetchAll();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.detail || "Błąd zapisu");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Usunąć to urządzenie?")) return;
    try {
      await axios.delete(`${API}/urzadzenia/${id}`);
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.detail || "Błąd usuwania");
    }
  };

  const handleAssign = async (item, employee_id) => {
    try {
      await axios.put(`${API}/urzadzenia/${item.id}`, { employee_id: employee_id || null });
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.detail || "Błąd przypisania");
    }
  };

  const openPrzekaz = (item) => {
    const today = new Date().toISOString().slice(0, 10).split('-').reverse().join('.');
    setPrzekazItem(item);
    setPrzekazForm({
      date: today,
      place: "Warszawa",
      stan_techniczny: "Dobry",
      uwagi: item.uwagi || "",
    });
  };

  const handleGeneratePrzekaz = async () => {
    if (!przekazItem) return;
    if (!przekazItem.employee_id) {
      if (!window.confirm("Urządzenie nie jest przypisane do pracownika. Wygenerować pusty protokół?")) return;
    }
    if (!przekazForm.date) {
      alert("Podaj datę");
      return;
    }
    setPrzekazSaving(true);
    try {
      const response = await axios.post(
        `${API}/urzadzenia/${przekazItem.id}/protokol`,
        przekazForm,
        { responseType: "blob" }
      );
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const empName = przekazItem.employee?.imie_nazwisko || przekazItem.employee?.name || "pracownik";
      a.download = `Protokol_przekazania_${przekazItem.typ}_${empName}.pdf`.replace(/\s+/g, "_");
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      setPrzekazItem(null);
      fetchAll();
    } catch (err) {
      console.error(err);
      alert("Błąd podczas generowania protokołu");
    } finally {
      setPrzekazSaving(false);
    }
  };

  const handleDownloadArchived = async (urz_id, protokol_id, filename) => {
    try {
      const response = await axios.get(`${API}/urzadzenia/${urz_id}/protokoly/${protokol_id}`, {
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "protokol.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert("Błąd podczas pobierania archiwalnego protokołu");
    }
  };

  const handleDeleteArchived = async (urz_id, protokol_id) => {
    if (!window.confirm("Usunąć ten protokół z archiwum?")) return;
    try {
      await axios.delete(`${API}/urzadzenia/${urz_id}/protokoly/${protokol_id}`);
      fetchAll();
    } catch (err) {
      alert("Błąd usuwania protokołu");
    }
  };

  const toggleExpanded = (id) => {
    setExpandedProtokoly((p) => ({ ...p, [id]: !p[id] }));
  };

  const openZniszczenie = (item) => {
    const today = new Date().toISOString().slice(0, 10).split('-').reverse().join('.');
    setZniszczenieItem(item);
    setZniszczenieForm({ date: today, place: "Warszawa", powod: "", sposob: "", uwagi: "" });
  };

  const handleGenerateZniszczenie = async () => {
    if (!zniszczenieItem) return;
    if (!zniszczenieForm.date) {
      alert("Podaj datę zniszczenia");
      return;
    }
    setZniszcSaving(true);
    try {
      const response = await axios.post(
        `${API}/urzadzenia/${zniszczenieItem.id}/protokol-zniszczenia`,
        zniszczenieForm,
        { responseType: "blob" }
      );
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const empName = zniszczenieItem.employee?.imie_nazwisko || zniszczenieItem.employee?.name || "pracownik";
      a.download = `Protokol_zniszczenia_${zniszczenieItem.typ}_${empName}.pdf`.replace(/\s+/g, "_");
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      setZniszczenieItem(null);
      fetchAll();
    } catch (err) {
      console.error(err);
      alert("Błąd podczas generowania protokołu zniszczenia");
    } finally {
      setZniszcSaving(false);
    }
  };

  const typLabel = (v) => DEVICE_TYPES.find((t) => t.value === v)?.label || v;

  if (loading) {
    return <div className="text-center py-12">Ładowanie...</div>;
  }

  return (
    <div className="max-w-[1400px] mx-auto px-3 py-4 sm:px-6 sm:py-6 space-y-4 sm:space-y-6" data-testid="urzadzenia-container">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl sm:text-3xl font-bold text-slate-800">Urządzenia</h2>
          <p className="text-xs sm:text-base text-slate-600 mt-1">
            Zarządzaj urządzeniami firmowymi i generuj protokoły przekazania.
          </p>
        </div>
        <button
          onClick={openNew}
          className="btn-primary flex items-center gap-2 justify-center"
          data-testid="urzadzenia-new-btn"
        >
          <Plus className="w-4 h-4" /> Dodaj urządzenie
        </button>
      </div>

      {items.length === 0 ? (
        <div className="bg-white rounded-xl p-8 text-center text-slate-500 shadow-sm border border-slate-200">
          {'Brak urządzeń. Kliknij „Dodaj urządzenie" aby utworzyć pierwszy wpis.'}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs font-semibold text-slate-600 uppercase">
                <th className="px-3 py-3">Typ</th>
                <th className="px-3 py-3">Model</th>
                <th className="px-3 py-3">Nr seryjny</th>
                <th className="px-3 py-3">Data zakupu</th>
                <th className="px-3 py-3 min-w-[180px]">Pracownik</th>
                <th className="px-3 py-3 text-right">Akcje</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((item) => {
                const protokoly = (item.protokoly || []).slice().sort((a, b) => (b.date || '').localeCompare(a.date || ''));
                const isExpanded = expandedProtokoly[item.id];
                return (
                <React.Fragment key={item.id}>
                <tr className="hover:bg-slate-50" data-testid={`urzadzenie-row-${item.id}`}>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-1.5">
                      {protokoly.length > 0 && (
                        <button
                          onClick={() => toggleExpanded(item.id)}
                          className="p-0.5 hover:bg-slate-200 rounded"
                          title={`${protokoly.length} archiwalnych protokołów`}
                          data-testid={`urzadzenie-expand-${item.id}`}
                        >
                          {isExpanded ? <CaretDown className="w-3.5 h-3.5" /> : <CaretRight className="w-3.5 h-3.5" />}
                        </button>
                      )}
                      <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-muted text-foreground rounded-md font-medium text-xs">
                        <DeviceMobile className="w-3.5 h-3.5" weight="bold" />
                        {typLabel(item.typ)}
                      </span>
                      {protokoly.length > 0 && (
                        <span className="text-[10px] text-slate-500 font-medium">
                          ({protokoly.length} prot.)
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-3 font-medium text-slate-800">{item.model || "—"}</td>
                  <td className="px-3 py-3 text-slate-600 font-mono text-xs">{item.numer_seryjny || "—"}</td>
                  <td className="px-3 py-3 text-slate-600">{item.data_zakupu || "—"}</td>
                  <td className="px-3 py-3">
                    <select
                      value={item.employee_id || ""}
                      onChange={(e) => handleAssign(item, e.target.value)}
                      className="px-2 py-1 border border-slate-300 rounded-sm text-xs w-full"
                      data-testid={`urzadzenie-assign-${item.id}`}
                    >
                      <option value="">— Nieprzypisane —</option>
                      {employees.map((emp) => (
                        <option key={emp._id} value={emp._id}>
                          {emp.imie_nazwisko ? `${emp.imie_nazwisko} (${emp.name})` : emp.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => openPrzekaz(item)}
                        title="Protokół przekazania"
                        className="p-1.5 hover:bg-emerald-100 text-emerald-700 rounded-sm"
                        data-testid={`urzadzenie-protokol-${item.id}`}
                      >
                        <FilePdf className="w-4 h-4" weight="bold" />
                      </button>
                      <button
                        onClick={() => openZniszczenie(item)}
                        title="Protokół zniszczenia"
                        className="p-1.5 hover:bg-orange-100 text-orange-700 rounded-sm"
                        data-testid={`urzadzenie-zniszczenie-${item.id}`}
                      >
                        <Fire className="w-4 h-4" weight="bold" />
                      </button>
                      <button
                        onClick={() => openEdit(item)}
                        title="Edytuj"
                        className="p-1.5 hover:bg-muted text-foreground rounded-sm"
                        data-testid={`urzadzenie-edit-${item.id}`}
                      >
                        <PencilSimple className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(item.id)}
                        title="Usuń"
                        className="p-1.5 hover:bg-red-100 text-red-700 rounded-sm"
                        data-testid={`urzadzenie-delete-${item.id}`}
                      >
                        <Trash className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
                {isExpanded && protokoly.length > 0 && (
                  <tr className="bg-slate-50">
                    <td colSpan={6} className="px-3 py-3">
                      <div className="pl-6">
                        <div className="text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wide">
                          Archiwum protokołów ({protokoly.length})
                        </div>
                        <div className="space-y-1.5">
                          {protokoly.map((p) => (
                            <div key={p.id} className="flex items-center justify-between p-2 bg-white rounded-md border border-slate-200 text-xs">
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <FilePdf className={`w-4 h-4 shrink-0 ${p.kind === 'zniszczenie' ? 'text-orange-600' : 'text-red-600'}`} />
                                <div className="min-w-0">
                                  <div className="font-medium text-slate-800 truncate flex items-center gap-1.5">
                                    {p.filename || 'protokol.pdf'}
                                    {p.kind === 'zniszczenie' && (
                                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded text-[9px] font-bold uppercase">
                                        Zniszczenie
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-[10px] text-slate-500">
                                    {new Date(p.date).toLocaleString('pl-PL')} · {p.employee_name || '—'}
                                    {p.employee_pesel ? ` · PESEL ${p.employee_pesel}` : ''}
                                  </div>
                                </div>
                              </div>
                              <div className="flex gap-1 shrink-0 ml-2">
                                <button
                                  onClick={() => handleDownloadArchived(item.id, p.id, p.filename)}
                                  className="p-1.5 hover:bg-muted text-foreground rounded-sm"
                                  title="Pobierz ponownie"
                                  data-testid={`archived-download-${p.id}`}
                                >
                                  <FilePdf className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => handleDeleteArchived(item.id, p.id)}
                                  className="p-1.5 hover:bg-red-100 text-red-700 rounded-sm"
                                  title="Usuń z archiwum"
                                  data-testid={`archived-delete-${p.id}`}
                                >
                                  <Trash className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto" data-testid="urzadzenie-modal">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-xl font-bold">
                {editing ? "Edytuj urządzenie" : "Nowe urządzenie"}
              </h3>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-muted rounded-sm">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="field-label block mb-2">Typ *</label>
                <select
                  value={form.typ}
                  onChange={(e) => setForm((p) => ({ ...p, typ: e.target.value }))}
                  className="input-field"
                  data-testid="urzadzenie-typ-input"
                >
                  {DEVICE_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="field-label block mb-2">Model</label>
                <input
                  type="text"
                  value={form.model}
                  onChange={(e) => setForm((p) => ({ ...p, model: e.target.value }))}
                  placeholder="np. iPhone 15 Pro / MacBook Air M2"
                  className="input-field"
                  data-testid="urzadzenie-model-input"
                />
              </div>
              <div>
                <label className="field-label block mb-2">Numer seryjny</label>
                <input
                  type="text"
                  value={form.numer_seryjny}
                  onChange={(e) => setForm((p) => ({ ...p, numer_seryjny: e.target.value }))}
                  className="input-field font-mono"
                  data-testid="urzadzenie-serial-input"
                />
              </div>
              <div>
                <label className="field-label block mb-2">Data zakupu</label>
                <input
                  type="date"
                  value={form.data_zakupu}
                  onChange={(e) => setForm((p) => ({ ...p, data_zakupu: e.target.value }))}
                  className="input-field"
                  data-testid="urzadzenie-date-input"
                />
              </div>
              <div>
                <label className="field-label block mb-2">Pracownik</label>
                <select
                  value={form.employee_id}
                  onChange={(e) => setForm((p) => ({ ...p, employee_id: e.target.value }))}
                  className="input-field"
                  data-testid="urzadzenie-employee-input"
                >
                  <option value="">— Nieprzypisane —</option>
                  {employees.map((emp) => (
                    <option key={emp._id} value={emp._id}>
                      {emp.imie_nazwisko ? `${emp.imie_nazwisko} (${emp.name})` : emp.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="field-label block mb-2">Uwagi</label>
                <textarea
                  value={form.uwagi}
                  onChange={(e) => setForm((p) => ({ ...p, uwagi: e.target.value }))}
                  rows={2}
                  className="input-field"
                  data-testid="urzadzenie-uwagi-input"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowModal(false)} className="btn-secondary flex-1">Anuluj</button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="btn-primary flex-1 flex items-center justify-center gap-2"
                data-testid="urzadzenie-save-btn"
              >
                {saving ? <CircleNotch className="w-4 h-4 animate-spin" /> : <FloppyDisk className="w-4 h-4" />}
                Zapisz
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Przekazanie Modal */}
      {przekazItem && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm w-full max-w-md p-6 max-h-[90vh] overflow-y-auto" data-testid="przekaz-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl font-bold flex items-center gap-2">
                <FilePdf className="w-5 h-5 text-emerald-600" weight="bold" />
                Protokół przekazania
              </h3>
              <button onClick={() => setPrzekazItem(null)} className="p-2 hover:bg-muted rounded-sm">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-md text-sm">
              <div className="font-semibold text-slate-800">
                {typLabel(przekazItem.typ)} — {przekazItem.model || '—'}
              </div>
              <div className="text-xs text-slate-600 mt-1">
                Nr seryjny: {przekazItem.numer_seryjny || '—'} · Pracownik: {przekazItem.employee?.imie_nazwisko || przekazItem.employee?.name || <span className="italic">nieprzypisane</span>}
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="field-label block mb-2">Data przekazania *</label>
                <input
                  type="text"
                  value={przekazForm.date}
                  onChange={(e) => setPrzekazForm((p) => ({ ...p, date: e.target.value }))}
                  placeholder="DD.MM.YYYY"
                  className="input-field"
                  data-testid="przekaz-date-input"
                />
              </div>
              <div>
                <label className="field-label block mb-2">Miejsce</label>
                <input
                  type="text"
                  value={przekazForm.place}
                  onChange={(e) => setPrzekazForm((p) => ({ ...p, place: e.target.value }))}
                  placeholder="Warszawa"
                  className="input-field"
                  data-testid="przekaz-place-input"
                />
              </div>
              <div>
                <label className="field-label block mb-2">Stan techniczny</label>
                <input
                  type="text"
                  value={przekazForm.stan_techniczny}
                  onChange={(e) => setPrzekazForm((p) => ({ ...p, stan_techniczny: e.target.value }))}
                  placeholder="np. Dobry, Bardzo dobry..."
                  className="input-field"
                  data-testid="przekaz-stan-input"
                />
              </div>
              <div>
                <label className="field-label block mb-2">Uwagi</label>
                <textarea
                  value={przekazForm.uwagi}
                  onChange={(e) => setPrzekazForm((p) => ({ ...p, uwagi: e.target.value }))}
                  rows={2}
                  className="input-field"
                  data-testid="przekaz-uwagi-input"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setPrzekazItem(null)} className="btn-secondary flex-1">Anuluj</button>
              <button
                onClick={handleGeneratePrzekaz}
                disabled={przekazSaving}
                className="btn-primary flex-1 flex items-center justify-center gap-2"
                data-testid="przekaz-generate-btn"
              >
                {przekazSaving ? <CircleNotch className="w-4 h-4 animate-spin" /> : <FilePdf className="w-4 h-4" />}
                Generuj PDF
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Zniszczenie Modal */}
      {zniszczenieItem && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm w-full max-w-md p-6 max-h-[90vh] overflow-y-auto" data-testid="zniszczenie-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl font-bold flex items-center gap-2">
                <Fire className="w-5 h-5 text-orange-600" weight="bold" />
                Protokół zniszczenia
              </h3>
              <button onClick={() => setZniszczenieItem(null)} className="p-2 hover:bg-muted rounded-sm">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-md text-sm">
              <div className="font-semibold text-slate-800">
                {typLabel(zniszczenieItem.typ)} — {zniszczenieItem.model || '—'}
              </div>
              <div className="text-xs text-slate-600 mt-1">
                Nr seryjny: {zniszczenieItem.numer_seryjny || '—'}
              </div>
              <div className="text-[10px] text-orange-700 mt-1 italic">
                W protokole zniszczenia pracownik nie jest wpisywany — dokument dotyczy tylko urządzenia.
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="field-label block mb-2">Data zniszczenia *</label>
                <input
                  type="text"
                  value={zniszczenieForm.date}
                  onChange={(e) => setZniszczenieForm((p) => ({ ...p, date: e.target.value }))}
                  placeholder="DD.MM.YYYY"
                  className="input-field"
                  data-testid="zniszczenie-date-input"
                />
              </div>
              <div>
                <label className="field-label block mb-2">Miejsce</label>
                <input
                  type="text"
                  value={zniszczenieForm.place}
                  onChange={(e) => setZniszczenieForm((p) => ({ ...p, place: e.target.value }))}
                  placeholder="Warszawa"
                  className="input-field"
                  data-testid="zniszczenie-place-input"
                />
              </div>
              <div>
                <label className="field-label block mb-2">Powód zniszczenia</label>
                <input
                  type="text"
                  value={zniszczenieForm.powod}
                  onChange={(e) => setZniszczenieForm((p) => ({ ...p, powod: e.target.value }))}
                  placeholder="np. Uszkodzenie mechaniczne, wyeksploatowanie..."
                  className="input-field"
                  data-testid="zniszczenie-powod-input"
                />
              </div>
              <div>
                <label className="field-label block mb-2">Sposób zniszczenia</label>
                <input
                  type="text"
                  value={zniszczenieForm.sposob}
                  onChange={(e) => setZniszczenieForm((p) => ({ ...p, sposob: e.target.value }))}
                  placeholder="np. Przekazano do utylizacji, zwrot do dostawcy..."
                  className="input-field"
                  data-testid="zniszczenie-sposob-input"
                />
              </div>
              <div>
                <label className="field-label block mb-2">Uwagi</label>
                <textarea
                  value={zniszczenieForm.uwagi}
                  onChange={(e) => setZniszczenieForm((p) => ({ ...p, uwagi: e.target.value }))}
                  rows={2}
                  className="input-field"
                  data-testid="zniszczenie-uwagi-input"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setZniszczenieItem(null)} className="btn-secondary flex-1">Anuluj</button>
              <button
                onClick={handleGenerateZniszczenie}
                disabled={zniszcSaving}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-sm transition-colors disabled:opacity-50"
                data-testid="zniszczenie-generate-btn"
              >
                {zniszcSaving ? <CircleNotch className="w-4 h-4 animate-spin" /> : <FilePdf className="w-4 h-4" />}
                Generuj PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
